import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { FeedbackNotification, Spinner } from '@toqio/toqio-web-components';
import { useBridge } from '../../../bridge/useBridge';
import { useFlowReady } from '../../../bridge/useFlowReady';
import { SecondaryButton } from '../../../shared/components';
import { resolveRevenueId } from '../api';
import { decodePispJwt } from '../jwt';

const FLOW_ID = 'external-bank';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 48px 24px;
  min-height: 60vh;
  max-width: 480px;
  margin: 0 auto;
  text-align: center;
`;

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
`;

/**
 * Read a string entry off `bridge.input`. Returns `null` when the key is
 * absent or not a string (defensive — lum-web spreads URL params, so any
 * key can legitimately be missing).
 */
const useStringInput = (key: string): string | null => {
  const bridge = useBridge();
  return useMemo(() => {
    const value = (bridge.input as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : null;
  }, [bridge.input, key]);
};

type Phase = 'processing' | 'error' | 'missing-params';

/**
 * Step 2 — `callback`.
 *
 * Volt redirects the payer back to a Toqio URL of the form
 *   …/embeddable-pay-by-bank-app?toqio=<JWT>&volt=<base64>&volt-signature=…&volt-timestamp=…
 * which lum-web spreads into `init.input` (one key per query param). We:
 *
 *   1. Forward `volt` token + signature + timestamp to
 *      `/api/v1/pisp/payments/revenue-id` to resolve the lum-wallet
 *      `revenueId` (the BE HMAC-verifies the token).
 *   2. Decode the `toqio` JWT to read the merchant `callbackUrl` claim.
 *   3. `navigate:native` to `${callbackUrl}?paymentId=<revenueId>` — that's
 *      the merchant's external page, outside Toqio.
 *
 * No visible result UI in the happy path: the user is gone within
 * milliseconds. A manual-redirect fallback button surfaces only when
 * `navigate:native` was emitted but the user is still here (e.g. the host
 * couldn't navigate).
 */
export const Step2Callback: React.FC = () => {
  const bridge = useBridge();
  const toqioJwt = useStringInput('toqio');
  const voltToken = useStringInput('volt');
  const voltSignature = useStringInput('volt-signature');
  const voltTimestamp = useStringInput('volt-timestamp');

  // The merchant's `callbackUrl` is a PISP JWT claim — there is no source
  // for it on the BE response, so we always derive it from the decoded JWT.
  const callbackUrl = useMemo(() => {
    if (!toqioJwt) return null;
    return decodePispJwt(toqioJwt)?.callbackUrl ?? null;
  }, [toqioJwt]);

  const hasAllInputs =
    Boolean(toqioJwt) &&
    Boolean(voltToken) &&
    Boolean(voltSignature) &&
    Boolean(voltTimestamp) &&
    Boolean(callbackUrl);

  const [phase, setPhase] = useState<Phase>(hasAllInputs ? 'processing' : 'missing-params');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [redirectTarget, setRedirectTarget] = useState<string | null>(null);
  const startedRef = useRef(false);
  const { signalReady } = useFlowReady();

  // Emit step:change on mount (bypassing StepWizard).
  useEffect(() => {
    bridge.emit({
      type: 'step:change',
      flowId: FLOW_ID,
      stepId: 'callback',
      stepIndex: 0,
      totalSteps: 1,
    });
    // Step 2 has no internal pre-paint loading state — every phase
    // (processing / error / missing-params) renders deliberate user-facing
    // content. Signal the host on mount so it can drop its overlay.
    signalReady();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Host back signal — block during processing, surface telemetry.
  useEffect(() => {
    return bridge.onBackRequest(() => {
      bridge.emit({
        type: 'telemetry',
        event: 'external-bank.callback.back-blocked',
      });
    });
  }, [bridge]);

  const navigateBack = useCallback(
    (route: string) => {
      bridge.emit({ type: 'navigate:native', route, params: { sameApp: true } });
    },
    [bridge],
  );

  useEffect(() => {
    if (!hasAllInputs || startedRef.current) return;
    // The guard checks already gated these to non-null strings.
    if (!toqioJwt || !voltToken || !voltSignature || !voltTimestamp || !callbackUrl) return;
    startedRef.current = true;

    (async () => {
      try {
        const { revenueId } = await resolveRevenueId(
          toqioJwt,
          voltToken,
          voltSignature,
          voltTimestamp,
        );
        if (!revenueId) {
          setErrorMessage(null);
          setPhase('error');
          bridge.emit({
            type: 'telemetry',
            event: 'external-bank.callback.error',
            props: { code: 'NO_REVENUE_ID', description: '' },
          });
          return;
        }
        const target = `${callbackUrl}?paymentId=${encodeURIComponent(revenueId)}`;
        setRedirectTarget(target);
        bridge.emit({
          type: 'telemetry',
          event: 'external-bank.callback.processed',
          props: { revenue_id: revenueId },
        });
        navigateBack(target);
      } catch (e) {
        setErrorMessage(null);
        setPhase('error');
        bridge.emit({
          type: 'telemetry',
          event: 'external-bank.callback.error',
          props: {
            code: 'NETWORK',
            description: e instanceof Error ? e.message : '',
          },
        });
      }
    })();
  }, [
    hasAllInputs,
    toqioJwt,
    voltToken,
    voltSignature,
    voltTimestamp,
    callbackUrl,
    bridge,
    navigateBack,
  ]);

  if (phase === 'missing-params') {
    return (
      <Page>
        <FeedbackNotification
          variant="error"
          message={t`We can't process this callback because the provider parameters are missing.`}
        />
      </Page>
    );
  }

  if (phase === 'error') {
    const message =
      errorMessage ?? t`We couldn't load the payment information. Please try again.`;
    return (
      <Page>
        <FeedbackNotification variant="error" message={message} />
        {redirectTarget && (
          <SecondaryButton size="m" onClick={() => navigateBack(redirectTarget)}>
            <Trans>Click here if you have not been redirected</Trans>
          </SecondaryButton>
        )}
      </Page>
    );
  }

  return (
    <Page>
      <SpinnerWrap>
        <Spinner />
      </SpinnerWrap>
      <Trans>Processing payment…</Trans>
      {redirectTarget && (
        <SecondaryButton size="s" onClick={() => navigateBack(redirectTarget)}>
          <Trans>Click here if you have not been redirected</Trans>
        </SecondaryButton>
      )}
    </Page>
  );
};
